import { ChevronDown, ChevronRight } from 'lucide-react'
import type { ChatOverview } from '@/types/teacher-chat'
import { formatDate } from '@/utils/chatHelpers'

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    pending: { label: 'Offen', cls: 'bg-yellow-100 text-yellow-800' },
    partially_accepted: { label: 'Teils', cls: 'bg-blue-100 text-blue-800' },
    accepted: { label: 'Angenommen', cls: 'bg-green-100 text-green-800' },
    declined: { label: 'Abgelehnt', cls: 'bg-red-100 text-red-800' },
    failed: { label: 'Gescheitert', cls: 'bg-red-100 text-red-800' },
    no_substitute_found: { label: 'Keine Vertretung', cls: 'bg-red-100 text-red-800' },
    cancelled: { label: 'Storniert', cls: 'bg-gray-100 text-gray-800' },
    expired: { label: 'Abgelaufen', cls: 'bg-gray-100 text-gray-800' },
    booked: { label: 'Gebucht', cls: 'bg-green-100 text-green-800' },
  }
  const s = map[status] || { label: status, cls: 'bg-gray-100 text-gray-800' }
  return (
    <span className={`px-1.5 py-0 text-[9px] rounded-full font-medium shrink-0 ${s.cls}`}>
      {s.label}
    </span>
  )
}

interface ChatGroupHeaderProps {
  chat: ChatOverview
  isExpanded: boolean
  onToggle: () => void
}

export function ChatGroupHeader({ chat, isExpanded, onToggle }: ChatGroupHeaderProps) {
  const subject = chat.subjects_summary || chat.subject_name || 'Fach'
  const klass = chat.classes_summary || chat.klass_name || '-'
  const ariaLabel = `Chat für ${subject} ${klass}, Status ${chat.request_status}${chat.unread_count > 0 ? `, ${chat.unread_count} ungelesene Nachrichten` : ''}`

  return (
    <div
      className="group-header px-3 py-2 border-b border-gray-100 flex items-center justify-between cursor-pointer hover:bg-gray-100/80 transition"
      onClick={onToggle}
      role="button"
      tabIndex={0}
      aria-expanded={isExpanded}
      aria-label={ariaLabel}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle() } }}
    >
      <div className="flex items-center gap-2 min-w-0">
        {isExpanded ? (
          <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" aria-hidden="true" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" aria-hidden="true" />
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-semibold text-gray-700 truncate">
              {subject} • {klass}
            </span>
            <StatusBadge status={chat.request_status} />
          </div>
          <div className="text-[10px] text-gray-400 truncate">
            {formatDate(chat.date)}{chat.is_multi_day && chat.date_to ? ` — ${formatDate(chat.date_to)}` : ''} · {chat.lesson_count} Lektion{chat.lesson_count !== 1 ? 'en' : ''}
            {chat.room_name ? ` · Raum ${chat.room_name}` : ''}
          </div>
        </div>
      </div>
      {chat.unread_count > 0 && (
        <span className="bg-blue-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0" aria-label={`${chat.unread_count} ungelesene Nachrichten`}>
          {chat.unread_count}
        </span>
      )}
    </div>
  )
}
